import { z } from 'zod';
import { chargeableFirstCleanPrice } from './first-clean-price.ts';
const pricingKeys = [
  'estimated_minutes',
  'billable_minutes',
  'routine_visit_minutes',
  'labor_subtotal_cents',
  'room_charges_cents',
  'fixed_fees_cents',
  'discount_cents',
  'frequency_discount_cents',
  'taxable_subtotal_cents',
  'tax_cents',
  'calculated_total_cents',
  'total_cents',
] as const;
const pricingSchema = z.object(
  Object.fromEntries(pricingKeys.map((key) => [key, z.number().int().min(0).max(100000000)])) as Record<
    (typeof pricingKeys)[number],
    z.ZodNumber
  >,
);
export const scopeTaskSchema = z.object({
  areaName: z.string(),
  areaNameI18n: z.string().nullable().default(null),
  taskNameI18n: z.string().nullable().default(null),
  descriptionI18n: z.string().nullable().default(null),
  taskName: z.string(),
  description: z.string().nullable(),
  frequency: z.string(),
  showOnCustomerQuote: z.boolean(),
  requiresPhoto: z.boolean().default(false),
  quantity: z.number().int().min(1),
});
const scopeSchema = z.object({
  sourceQuoteId: z.string(),
  sourceVersion: z.number().int().min(1),
  currencyCode: z.string(),
  crewSize: z.number().int().min(1),
  pricing: pricingSchema,
  tasks: z.array(scopeTaskSchema).min(1),
  zeroPriceApproved: z.boolean(),
});
export type FirstCleanScope = z.infer<typeof scopeSchema>;
const optionSchema = z.object({
  id: z.string(),
  name: z.string().min(1).max(120),
  firstVisit: scopeSchema,
  routine: scopeSchema,
  /**
   * The first clean extra inside the first visit's fees, shown as its own
   * line. Named for the square-foot quotes that had it first; any option built
   * from a quote's own first clean price carries it, 0 when the first visit
   * is priced by its first-visit-only tasks alone.
   */
  areaFirstCleanExtraCents: z.number().int().min(0).optional(),
});

export function firstCleanOptionName(
  option: { name: string; areaFirstCleanExtraCents?: number | undefined },
  language: string,
): string {
  if (option.areaFirstCleanExtraCents === undefined) return option.name;
  return language === 'es' ? 'Primera limpieza' : 'First clean';
}
export type FirstCleanOption = z.infer<typeof optionSchema> & { firstVisit: FirstCleanScope; routine: FirstCleanScope };
const bundleSchema = z.object({
  revision: z.string(),
  reviewed: z.boolean(),
  reviewedAt: z.string(),
  reviewedByUserId: z.string(),
  customerId: z.string(),
  propertyId: z.string().nullable(),
  currencyCode: z.string(),
  options: z.array(optionSchema).min(1).max(5),
  /**
   * Set when the bundle was built from the quote itself (its first clean price
   * and first-visit-only tasks) rather than from separate scope quotes. The
   * quote save rebuilds it every time; this records what the single option was
   * priced from, so a change that reached the quote some other way makes the
   * offer stale instead of charging old numbers. Named for the square-foot
   * quotes that had it first.
   */
  areaFirstClean: z
    .object({
      kind: z.enum(['fixed', 'percent']),
      value: z.number().int().min(0),
      regularTotalCents: z.number().int().min(0),
    })
    .optional(),
});
export type FirstCleanBundle = z.infer<typeof bundleSchema> & { options: FirstCleanOption[] };
const acceptedSchema = optionSchema.extend({ bundleRevision: z.string(), acceptedVersion: z.number().int().min(1) });
export type AcceptedFirstClean = z.infer<typeof acceptedSchema> & FirstCleanOption;
export function readFirstCleanBundle(json: string | null | undefined): FirstCleanBundle | null {
  return json ? (bundleSchema.parse(JSON.parse(json)) as FirstCleanBundle) : null;
}
export function readAcceptedFirstClean(json: string | null | undefined): AcceptedFirstClean | null {
  return json ? (acceptedSchema.parse(JSON.parse(json)) as AcceptedFirstClean) : null;
}
export function firstCleanBlocked(quote: {
  first_clean_options_json?: string | null;
  accepted_first_clean_json?: string | null;
  first_clean_price_json?: string | null;
  customer_id: string;
  property_id: string | null;
  currency_code: string;
  billing_basis: string;
  frequency_tiers_enabled: number;
  price_promise?: string | null;
  preferred_frequency?: string | null;
  status: string;
  total_cents?: number;
}) {
  const agreed = quote.status === 'accepted' || quote.status === 'completed';
  if (agreed && readAcceptedFirstClean(quote.accepted_first_clean_json)) return false;
  if (firstCleanPricePending(quote)) return true;
  const bundle = readFirstCleanBundle(quote.first_clean_options_json);
  if (!bundle) return false;
  return (
    !bundle.reviewed ||
    bundle.customerId !== quote.customer_id ||
    bundle.propertyId !== quote.property_id ||
    bundle.currencyCode !== quote.currency_code ||
    quote.billing_basis !== 'per_visit' ||
    quote.frequency_tiers_enabled === 1 ||
    quote.price_promise === 'range' ||
    quoteFirstCleanStale(bundle, quote)
  );
}

/**
 * A first clean price with no current offer built from it. The quote save
 * builds one whenever it can; it cannot while the quote offers frequency
 * choices, promises a range, has its total overridden, or has no tasks for
 * the regular visit. Sharing or accepting such a quote would show and bill
 * the regular price while the estimator expects the first clean price.
 *
 * A quote already agreed is not reopened by this: it was accepted on the
 * terms it showed.
 */
export function firstCleanPricePending(quote: {
  first_clean_price_json?: string | null;
  billing_basis?: string;
  first_clean_options_json?: string | null;
  status?: string;
}): boolean {
  if (quote.status === 'accepted' || quote.status === 'completed') return false;
  if (chargeableFirstCleanPrice(quote) === null) return false;
  return !readFirstCleanBundle(quote.first_clean_options_json)?.areaFirstClean;
}

function quoteFirstCleanStale(
  bundle: FirstCleanBundle,
  quote: {
    first_clean_price_json?: string | null;
    billing_basis?: string;
    preferred_frequency?: string | null;
    total_cents?: number;
  },
): boolean {
  const current = chargeableFirstCleanPrice(quote);
  const offered = bundle.areaFirstClean;
  // Separate scope alternatives and a first clean price never combine: the
  // customer would see one and be billed the other.
  if (!offered) return current !== null;
  return (
    current === null ||
    // No regular visits follow a one-time clean, so an offer built before the
    // save required a repeating schedule (0105) cannot stand on one.
    !quote.preferred_frequency ||
    quote.preferred_frequency === 'one_time' ||
    current.kind !== offered.kind ||
    current.value !== offered.value ||
    quote.total_cents === undefined ||
    quote.total_cents !== offered.regularTotalCents
  );
}
const seriesFirstCleanSchema = acceptedSchema.extend({
  firstOccurrenceDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  firstDurationMinutes: z.number().int().min(1).max(1440),
});
export type SeriesFirstClean = z.infer<typeof seriesFirstCleanSchema>;
export function readSeriesFirstClean(json: string | null | undefined): SeriesFirstClean | null {
  return json ? seriesFirstCleanSchema.parse(JSON.parse(json)) : null;
}
export function frozenScopeForOccurrence(json: string | null | undefined, date: string) {
  const choice = readSeriesFirstClean(json);
  if (!choice) return null;
  const first = date === choice.firstOccurrenceDate;
  return {
    kind: first ? ('first_visit' as const) : ('routine' as const),
    optionId: choice.id,
    bundleRevision: choice.bundleRevision,
    scope: first ? choice.firstVisit : choice.routine,
  };
}
const visitScopeSchema = z.object({
  kind: z.enum(['first_visit', 'routine']),
  optionId: z.string(),
  bundleRevision: z.string(),
  scope: scopeSchema,
});
export function readFrozenVisitScope(json: string | null | undefined) {
  return json ? visitScopeSchema.parse(JSON.parse(json)) : null;
}
export const bookingProgressSchema = z.object({
  quoteVersion: z.number().int().min(1),
  acceptedAt: z.string().nullable(),
  acceptedFirstCleanJson: z.string().nullable(),
  requiredDates: z
    .array(z.string().regex(/^\d{4}-\d{2}-\d{2}$/))
    .min(1)
    // Bound recovery receipts while admitting the supported nine-month horizon.
    .max(280),
  mode: z.enum(['schedule', 'single_job']),
  completionId: z.string().optional(),
  completedAt: z.string().nullable(),
});
export type QuoteBookingProgress = z.infer<typeof bookingProgressSchema>;
export function readBookingProgress(json: string | null | undefined): QuoteBookingProgress | null {
  return json ? bookingProgressSchema.parse(JSON.parse(json)) : null;
}
